import { ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import theme from '../../../../theme/theme';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import type { ReactNode } from 'react';

interface SideMenuItemProps {
  label: string;
  icon: ReactNode;
  selected: boolean;
  showLabel: boolean;
  hasSubItems: boolean;
  isExpanded: boolean;
  onClick: () => void;
}

export function SideMenuItem({
  label,
  icon,
  selected,
  showLabel,
  hasSubItems,
  isExpanded,
  onClick,
}: SideMenuItemProps) {
  const selectedBg = theme.navigation.main.selected;
  const hoverAccentColor = theme.navigation.main.selectedAccent;
  const selectedBorder = theme.navigation.main.selectedBorder;

  return (
    <ListItemButton
      selected={selected}
      onClick={onClick}
      aria-current={selected ? 'page' : undefined}
      aria-expanded={hasSubItems ? isExpanded : undefined}
      sx={{
        minHeight: theme.toolbar.height,
        pl: { xs: theme.spacing(2.5), sm: theme.spacing(2) },
        pr: { xs: theme.spacing(2.5), sm: theme.spacing(2) },
        justifyContent: showLabel ? 'initial' : 'center',
        color: theme.palette.text.label,
        // Bordure toujours présente pour éviter le layout shift
        borderLeft: '4px solid transparent',
        transition: 'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease',
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
        },

        '&:hover': {
          bgcolor: theme.navigation.main.hover,
          color: hoverAccentColor,
        },
        '&:focus-visible': {
          outline: `2px solid ${selectedBorder}`,
          outlineOffset: '2px',
        },
        '&.Mui-selected': {
          bgcolor: selectedBg,
          color: hoverAccentColor,
          borderLeft: `4px solid ${selectedBorder}`,
        },
        '&.Mui-selected:hover': {
          bgcolor: selectedBg,
          color: hoverAccentColor,
          borderLeft: `4px solid ${selectedBorder}`,
        },
        '&:hover .MuiListItemIcon-root': {
          color: hoverAccentColor,
        },
        '&.Mui-selected .MuiListItemIcon-root': {
          color: hoverAccentColor,
        },
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: theme.toolbar.height,
          mr: showLabel ? 3 : 'auto',
          justifyContent: 'center',
          color: theme.palette.text.label,
          flexShrink: 0,
          transition: 'color 0.15s ease, margin 300ms cubic-bezier(0.4, 0, 0.2, 1)',
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      >
        {icon}
      </ListItemIcon>
      <ListItemText
        primary={label}
        sx={{
          ml: 2,
          whiteSpace: 'nowrap',
          opacity: showLabel ? 1 : 0,
          width: showLabel ? 'auto' : 0,
          overflow: 'hidden',
          transition:
            'opacity 200ms cubic-bezier(0.4, 0, 0.2, 1), width 300ms cubic-bezier(0.4, 0, 0.2, 1)',
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      />
      {hasSubItems && showLabel && (isExpanded ? <ExpandLess /> : <ExpandMore />)}
    </ListItemButton>
  );
}
